/**
 * What the browser uploads for a server-side export — shared by the compare
 * page (which writes it) and export-worker (which reads it), so the two can't
 * disagree about its shape. The drugs are already gathered and carry the
 * user's manual highlights. See docs/export/SERVER_SIDE_EXPORT_PLAN.md §5.
 */
import type { ExportDrug } from './compareExport';
import type { ColoredTerm } from './highlightColors';

export type ServerExportFormat = 'xlsx' | 'docx' | 'html';

export interface ExportPayload {
  version: 1;
  format: ServerExportFormat;
  drugs: ExportDrug[];
  keyword: string;
  section?: string;
  coloredTerms?: ColoredTerm[];
  includePageText?: boolean;
}

export function parseExportPayload(json: unknown): ExportPayload {
  const p = json as Partial<ExportPayload>;
  if (!p || p.version !== 1) throw new Error('Unsupported export payload version');
  if (!p.format || !['xlsx', 'docx', 'html'].includes(p.format)) throw new Error('Export payload has no valid format');
  if (!Array.isArray(p.drugs) || p.drugs.length === 0) throw new Error('Export payload has no drugs');
  return { ...p, keyword: p.keyword ?? '' } as ExportPayload;
}

/** gzip in the browser (CompressionStream) — label HTML compresses ~5-10x,
 *  which keeps a 50-drug payload a few MB instead of tens. */
export async function gzipJson(value: unknown): Promise<Blob> {
  const stream = new Response(JSON.stringify(value)).body!.pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).blob();
}

/** PUT the payload straight to GCS with the signed URL — never through the BFF,
 *  whose request-size limit a big payload would exceed. */
export async function uploadPayload(url: string, contentType: string, body: Blob, signal?: AbortSignal): Promise<void> {
  const resp = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body, ...(signal ? { signal } : {}) });
  if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
}
